import { useEffect, useRef } from 'react';
import {
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  type MetricWithAttribution,
} from 'web-vitals/attribution';

interface CoreWebVitalsProps {
  cwvIndicator?: string;
}

interface MappedCwv {
  name: string;
  debugTarget: string;
  testGroup: string;
  id: string;
  navigationId: number;
  navigationType: MetricWithAttribution['navigationType'];
  navigationUrl: string;
  value: number;
  delta: number;
  valueRounded: number;
  deltaRounded: number;
  attribution?: MetricWithAttribution['attribution'];
}

const getDebugTarget = (cwv: MetricWithAttribution) => {
  switch (cwv.name) {
    case 'CLS':
      return cwv.attribution.largestShiftTarget || '';
    case 'LCP':
      return cwv.attribution.target || '';
    case 'INP':
      return cwv.attribution.interactionTarget || '';
    default:
      return '';
  }
};

export default function CoreWebVitals({ cwvIndicator = '' }: CoreWebVitalsProps) {
  // Prevent duplicate event attachment
  const eventsAttached = useRef(false);
  useEffect(() => {
    if (eventsAttached.current) {
      return;
    }
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const debugMode = urlParams.get('cwv_debug') === '1';
      const getTestGroup = () => cwvIndicator;

      const mapToDataLayer = (cwv: MetricWithAttribution) => {
        window.dataLayer = window.dataLayer || [];
        const mappedCwv: MappedCwv = {
          name: cwv.name,
          debugTarget: '',
          testGroup: '',
          id: cwv.id,
          navigationId: cwv.navigationId,
          navigationType: cwv.navigationType,
          navigationUrl: cwv.navigationURL || window.location.href,
          value: cwv.value,
          delta: cwv.delta,
          valueRounded: Math.round(cwv.name === 'CLS' ? cwv.value * 1000 : cwv.value),
          deltaRounded: Math.round(cwv.name === 'CLS' ? cwv.delta * 1000 : cwv.delta),
        };
        mappedCwv.debugTarget = getDebugTarget(cwv);
        mappedCwv.testGroup = getTestGroup();

        // eslint-disable-next-line no-undef
        if (!debugMode) {
          window.dataLayer.push({ event: 'coreWebVitals', webVitalsMeasurement: mappedCwv });
        } else {
          mappedCwv.attribution = cwv.attribution;
          console.log(mappedCwv);
        }
      };

      onCLS(mapToDataLayer, { reportAllChanges: debugMode, reportSoftNavs: true });
      onFCP(mapToDataLayer);
      onINP(mapToDataLayer, { reportAllChanges: debugMode, reportSoftNavs: true });
      onLCP(mapToDataLayer, { reportSoftNavs: true });
      onTTFB(mapToDataLayer);
      eventsAttached.current = true;
    }
  }, [cwvIndicator, eventsAttached]);

  return null;
}
